<!-- eslint-disable @angular-eslint/template/cyclomatic-complexity -->
<!--
  VIEW MANAGER CYCLE FORM COMPONENT

  Displays cycle forms assigned to managers with:
  - Breadcrumb navigation
  - Form list sidebar with completion stats
  - Activity tabs for multi-activity forms
  - Read-only question/answer display
  - Overall input tracking (root cause, mitigation)
  - Decision approval workflow for Compliance BP

  Note: Cyclomatic complexity ESLint rule is disabled for this file as it contains
  many conditional display sections that are necessary for the read-only view.
  Future refactoring could extract sections into child components.
-->

<div class="manager-cycle-form-container">
  <!-- ==================== BREADCRUMB NAVIGATION ==================== -->
  <nav class="breadcrumb-nav" aria-label="Breadcrumb">
    <a routerLink="/dashboard" class="breadcrumb-link">
      <mat-icon class="breadcrumb-icon">home</mat-icon>
      Dashboard
    </a>
    @if (!isManager) {
      <mat-icon class="breadcrumb-separator">chevron_right</mat-icon>
      <a tabindex="0"
         role="button"
         class="breadcrumb-link breadcrumb-clickable"
         (click)="navigateToCycles()"
         (keyup.enter)="navigateToCycles()"
         (keyup.space)="navigateToCycles()">
        <mat-icon class="breadcrumb-icon">assignment</mat-icon>
        Cycles
      </a>
      <mat-icon class="breadcrumb-separator">chevron_right</mat-icon>
      <a tabindex="0"
         role="button"
         class="breadcrumb-link breadcrumb-clickable breadcrumb-with-ellipsis"
         tooltipPosition="below"
         [appBefairTooltip]="formData.cycleName || 'Cycle Assignments'"
         (click)="navigateToCycleDetails()"
         (keyup.enter)="navigateToCycleDetails()"
         (keyup.space)="navigateToCycleDetails()">
        <mat-icon class="breadcrumb-icon">assignment_turned_in</mat-icon>
        <span class="breadcrumb-text-ellipsis">{{ formData.cycleName || 'Cycle Assignments' }}</span>
      </a>
    }
    <mat-icon class="breadcrumb-separator">chevron_right</mat-icon>
    <span class="breadcrumb-current">{{ formData.managerName || 'Manager Cycle Form' }}</span>
  </nav>

  <!-- ==================== HEADER SECTION ==================== -->
  <div class="header-section">
    <div class="header-content">
      <div class="title-container">
        <h1 class="page-title">{{ formData.cycleName }}</h1>
        <mat-icon class="help-icon" tooltipPosition="right"
          [appBefairTooltip]="'View and manage your cycle forms, track progress, and update answers.'">
          help_outline
        </mat-icon>
      </div>
      <p class="page-subtitle">View and manage your cycle forms and track completion status</p>
    </div>
  </div>

  <!-- ==================== MAIN CONTENT LAYOUT ==================== -->
  <div class="content-wrapper">
    <div class="main-content">
      <!-- ========== SIDEBAR - Forms List ========== -->
      <div class="sidebar">
        <div class="sidebar-header">
          <mat-icon class="sidebar-icon">assignment</mat-icon>
          <h3 class="sidebar-title">{{ totalForms }} {{ totalForms === 1 ? 'Form' : 'Forms' }}</h3>
        </div>

        <div class="forms-list" role="list">
          @for (form of formData.forms; track $index) {
            <button class="form-item"
                    [class.selected]="form.isSelected"
                    [class.active]="form.isSelected"
                    [attr.aria-label]="'Select form: ' + form.name"
                    [attr.aria-current]="form.isSelected ? 'true' : 'false'"
                    (click)="selectForm(form)">
              <div class="form-indicator">
                <span class="form-number">{{ $index + 1 }}</span>
              </div>
              <div class="form-info">
                <div class="form-name">{{ form.name }}</div>
                <div class="form-meta">
                  <span>{{ form.activity_count || 0 }} activities</span>
                  <span class="completion-text">{{ form.completion_percentage || 0 }}%</span>
                  @if (formCompletedCache.get(form.id)) {
                    <span class="completed-badge" title="Completed"><mat-icon>check_circle</mat-icon></span>
                  }
                  @if (formNotApplicableCache.get(form.id)) {
                    <span class="no-answers-badge">Not Applicable</span>
                  }
                  @if (formNoAnswersCache.get(form.id)) {
                    <span class="no-answers-badge">No: {{ formNoAnswersCache.get(form.id) }}</span>
                  }
                  @if (isComplaintsDP && formPendingApprovalsCache.get(form.id)) {
                    <span class="approval-badge pending" [appBefairTooltip]="formPendingApprovalsCache.get(form.id) + (formPendingApprovalsCache.get(form.id) === 1 ? ' activity pending approval' : ' activities pending approval')">
                      <mat-icon class="approval-icon">pending_actions</mat-icon>
                      {{ formPendingApprovalsCache.get(form.id) }}
                    </span>
                  }
                </div>
              </div>
              <mat-icon class="chevron-icon">chevron_right</mat-icon>
            </button>
          }
        </div>
      </div>

      <!-- ========== MAIN CONTENT AREA ========== -->
      <div class="content-panel">
        <!-- Manager Summary Card -->
        <section class="summary-section">
          <div class="manager-summary-card">
            <div class="card-header">
              <h3 class="card-title">
                <mat-icon class="card-icon">person</mat-icon>
                Manager Overview
              </h3>
            </div>
            <div class="summary-grid">
              <div class="summary-item">
                <mat-icon class="summary-icon">person</mat-icon>
                <div class="summary-content">
                  <span class="summary-label">Manager</span>
                  <span class="summary-value">{{ formData.managerName }}</span>
                </div>
              </div>

              <div class="summary-item">
                <mat-icon class="summary-icon">group</mat-icon>
                <div class="summary-content">
                  <span class="summary-label">Activities</span>
                  <span class="summary-value">{{ totalActivityOwners }}</span>
                </div>
              </div>

              <div class="summary-item">
                <mat-icon class="summary-icon">schedule</mat-icon>
                <div class="summary-content">
                  <span class="summary-label">Deadline</span>
                  <span class="summary-value">{{ managerDeadline }}</span>
                </div>
              </div>

              <div class="summary-item completion-item">
                <mat-icon class="summary-icon">analytics</mat-icon>
                <div class="summary-content">
                  <span class="summary-label">Completion Status</span>
                  <div class="completion-status-container">
                    <div class="progress-bar">
                      <div class="progress-fill" [ngClass]="completionStatusClass" [style.width.%]="overallCompletion"></div>
                    </div>
                    <span class="status-text">{{ overallCompletion }}% Complete</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Form Content -->
        @if (selectedForm) {
          <section class="form-section">
            <div class="form-content">
              <!-- Form Header with Title and Activity Tabs -->
              <div class="form-header">
                <div class="form-title-container">
                  <h2 class="form-title">
                    <mat-icon class="form-icon">description</mat-icon>
                    {{ selectedForm.name }}
                    @if (selectedForm.is_form_not_applicable) {
                      <span class="not-applicable-badge" [appBefairTooltip]="selectedForm.form_not_applicable_reason || 'This form is not applicable'">
                        <mat-icon>block</mat-icon>
                        Not Applicable
                      </span>
                    }
                  </h2>
                </div>

                <!-- Activity Tabs -->
                @if (activityTabs.length > 0 && !selectedForm.is_form_not_applicable) {
                  <div class="activity-tabs">
                    @for (tab of activityTabs; track tab.activity_number; let i = $index) {
                      <app-activity-tab
                        [activityNumber]="tab.activity_number"
                        [completionCount]="tab.total_answers"
                        [totalQuestions]="totalQuestionsCount"
                        [noAnswerCount]="tab.no_answers"
                        [activityStatus]="tab.activity_status"
                        [displayName]="tab.display_name"
                        [isActive]="i === selectedActivityIndex"
                        (selectTab)="selectActivity(i)" />
                    }
                  </div>
                }
              </div>

              <!-- Form Not Applicable Message -->
              @if (selectedForm.is_form_not_applicable) {
                <div class="empty-state-card">
                  <mat-icon class="empty-icon">block</mat-icon>
                  <h3>This form has been marked as "Not Applicable" by the Manager</h3>
                  <p>{{ selectedForm.form_not_applicable_reason || 'This form has been marked as not applicable.' }}</p>
                </div>
              } @else {

              <!-- ========== EMPTY STATES ========== -->
              <!-- No Activities Message -->
              @if (!hasActivities && !selectedForm.is_form_not_applicable) {
                <div class="empty-state-card">
                  <mat-icon class="empty-icon">info_outline</mat-icon>
                  <h3>No Activities Available</h3>
                  <p>There are no activities recorded for this form yet. Questions with answers will be displayed once activities are added.</p>
                </div>
              }

              <!-- ========== FILTER CONTROLS & ACTIVITY DETAILS ========== -->
              @if (hasActivities && !selectedForm.is_form_not_applicable) {
                <div class="filter-section">
                  @if (activityTabs[selectedActivityIndex]) {
                    <div class="activity-info-inline">
                      <div class="activity-detail-item">
                        <mat-icon class="detail-icon">badge</mat-icon>
                        <span class="detail-label">Reference Number:</span>
                        <span class="detail-value">{{ activityTabs[selectedActivityIndex].reference_number || 'N/A' }}</span>
                      </div>
                      <div class="activity-detail-item">
                        <mat-icon class="detail-icon">person</mat-icon>
                        <span class="detail-label">Activity Owner:</span>
                        <span class="detail-value">{{ activityTabs[selectedActivityIndex].display_name || 'N/A' }}</span>
                      </div>
                    </div>
                  }

                  <mat-checkbox
                    class="filter-checkbox"
                    [(ngModel)]="formData.showOnlyAnswersWithNo"
                    (ngModelChange)="onFilterChange()">
                    <mat-icon class="filter-icon">filter_list</mat-icon>
                    Show only Answers with "No"
                  </mat-checkbox>
                </div>
              }

              <!-- ========== QUESTIONS BY STAGE ========== -->
              @if (hasActivities && !selectedForm.is_form_not_applicable) {
                <div class="stages-container">
                @for (stage of allStages; track stage.id) {
                  <div class="stage-group">
                    <div class="stage-header">
                      <mat-icon class="stage-icon">layers</mat-icon>
                      <h3 class="stage-title">{{ stage.name }}</h3>
                      <span class="stage-count">{{ stage.questions.length }} Questions</span>
                    </div>

                    <!-- Questions List -->
                    <div class="questions-container">
                      @for (question of stage.questions; track question.id; let qIdx = $index) {
                        @if (question.showQuestion) {
                          <div class="question-card">
                            <div class="question-header">
                              <div class="question-content">
                                <span class="question-text">{{ question.questionNumber }}. {{ question.text }}</span>
                              </div>
                            </div>

                            <div class="question-answer">
                              <div class="answer-section">
                                <!-- Display answer for yes_no and yes_no_na -->
                                @if (question.type === 'yes_no' || question.type === 'yes_no_na') {
                                  <div class="answer-display">
                                    <span class="answer-label">Answer:</span>
                                    <span class="answer-value" [class.answer-yes]="question.answer === 'yes'"
                                          [class.answer-no]="question.answer === 'no'"
                                          [class.answer-na]="question.answer === 'na'">
                                      {{ question.answerText || 'No answer provided' }}
                                    </span>
                                  </div>
                                }

                                <!-- Display answer for text type -->
                                @if (question.type === 'text') {
                                  <div class="answer-display">
                                    <span class="answer-label">Answer:</span>
                                    <span class="answer-value text-answer-value">
                                      {{ question.answer || 'No answer provided' }}
                                    </span>
                                  </div>
                                }

                                <!-- Display answer for number type -->
                                @if (question.type === 'number') {
                                  <div class="answer-display">
                                    <span class="answer-label">Answer:</span>
                                    <span class="answer-value">
                                      {{ question.answer !== undefined ? question.answer : 'No answer provided' }}
                                    </span>
                                  </div>
                                }
                              </div>

                              <!-- Comments Section -->
                              @if (question.comments) {
                                <div class="comments-display">
                                  <div class="comments-header">
                                    <mat-icon class="label-icon">comment</mat-icon>
                                    <span class="comments-label">Comments</span>
                                  </div>
                                  <div class="comments-text">
                                    {{ question.comments }}
                                  </div>
                                </div>
                              }

                              <!-- Attachment Section -->
                              @if (question.attachment) {
                                <div class="attachment-section">
                                  <div class="attachment-label">
                                    <mat-icon class="label-icon">attach_file</mat-icon>
                                    Attachment
                                  </div>
                                  <div class="attachment-item">
                                    <mat-icon class="attachment-icon">description</mat-icon>
                                    <span class="attachment-name">{{ question.attachment }}</span>
                                    <button mat-icon-button
                                            class="download-btn"
                                            tooltipPosition="above"
                                            [appBefairTooltip]="'Download file'"
                                            [attr.aria-label]="'Download ' + question.attachment"
                                            (click)="downloadAttachment(question)">
                                      <mat-icon>download</mat-icon>
                                    </button>
                                  </div>
                                </div>
                              }
                            </div>
                          </div>
                        }
                      }
                    </div>
                  </div>
                }
              </div>

              <!-- ========== OVERALL INPUT - Read Only Display ========== -->
              @if (shouldShowOverallInput) {
                <div class="overall-input-display">
                  <div class="overall-input-header">
                    <mat-icon class="section-icon">assessment</mat-icon>
                    <h3>Overall Input</h3>
                  </div>

                  <div class="overall-input-grid">
                    <!-- Root Cause -->
                    <div class="input-display-group">
                      <div class="input-label">
                        <mat-icon class="label-icon">flag</mat-icon>
                        Root Cause
                      </div>
                      <div class="input-value">
                        {{ rootCauseDisplayText }}
                      </div>
                    </div>

                    <!-- Mitigation Action -->
                    <div class="input-display-group">
                      <div class="input-label">
                        <mat-icon class="label-icon">build</mat-icon>
                        Mitigation Action
                      </div>
                      <div class="input-value">
                        {{ mitigationActionDisplayText }}
                      </div>
                    </div>

                    <!-- Monitoring Status -->
                    <div class="input-display-group">
                      <div class="input-label">
                        <mat-icon class="label-icon">check_circle</mat-icon>
                        Monitoring Complete
                      </div>
                      <div class="input-value">
                        <span class="status-badge" [class.confirmed]="isMonitoringConfirmed">
                          <mat-icon>{{ monitoringStatusIcon }}</mat-icon>
                          {{ monitoringStatusText }}
                        </span>
                      </div>
                    </div>

                    <!-- Comments -->
                    @if (currentActivityOverallInput.comments) {
                      <div class="input-display-group full-width">
                        <div class="input-label">
                          <mat-icon class="label-icon">comment</mat-icon>
                          Comments
                        </div>
                        <div class="input-value comments-value">
                          {{ currentActivityOverallInput.comments }}
                        </div>
                      </div>
                    }
                  </div>
                </div>
              }

              <!-- ========== DECISION STATUS - Read Only Display ========== -->
              @if (shouldShowDecisionStatusSection) {
                <div class="overall-input-display decision-status-display">
                  <div class="overall-input-header">
                    <mat-icon class="section-icon">gavel</mat-icon>
                    <h3>Decision Status</h3>
                  </div>

                  <div class="overall-input-grid">
                    <!-- Decision Status Badge -->
                    <div class="input-display-group full-width">
                      <div class="input-label">
                        <mat-icon class="label-icon">gavel</mat-icon>
                        Status
                      </div>
                      <div class="input-value">
                        <div class="status-badge decision-badge"
                             [class.approved]="isApprovedStatus"
                             [class.rejected]="isRejectedStatus">
                          <mat-icon>{{ decisionStatusIcon }}</mat-icon>
                          {{ decisionStatusText }}
                        </div>
                      </div>
                    </div>

                    <!-- Approver Information -->
                    @if (currentActivityOverallInput.approvedBy) {
                      <div class="input-display-group full-width">
                        <div class="input-label">
                          <mat-icon class="label-icon">person</mat-icon>
                          {{ approverLabel }}
                        </div>
                        <div class="input-value">
                          {{ currentActivityOverallInput.approvedBy }}
                        </div>
                      </div>
                    }

                    <!-- Approver Comments -->
                    @if (currentActivityOverallInput.approverComments) {
                      <div class="input-display-group full-width">
                        <div class="input-label">
                          <mat-icon class="label-icon">comment</mat-icon>
                          Compliance BP Comments
                        </div>
                        <div class="input-value comments-value">
                          {{ currentActivityOverallInput.approverComments }}
                        </div>
                      </div>
                    }
                  </div>
                </div>
              }

              <!-- ========== DECISION WORKFLOW - Compliance BP Only ========== -->
              @if (shouldShowDecisionSection) {
                <div class="decision-section">
                  <div class="decision-header">
                    <mat-icon class="section-icon">gavel</mat-icon>
                    <h3>Decision</h3>
                  </div>

                  @if (activityDecisionStatus) {
                    <!-- Show decision status if already decided -->
                    <div class="decision-status">
                      <div class="status-message" [class.accepted]="isDecisionAccepted"
                           [class.rejected]="isDecisionRejected">
                        <mat-icon>{{ decisionWorkflowStatusIcon }}</mat-icon>
                        <span>{{ decisionWorkflowStatusText }}</span>
                      </div>
                      @if (decisionComments) {
                        <div class="decision-comments-display">
                          <strong>Comments:</strong> {{ decisionComments }}
                        </div>
                      }
                    </div>
                  } @else {
                    <!-- Show decision form if not yet decided -->
                    <div class="decision-form">
                      <mat-form-field appearance="outline" class="comments-field">
                        <mat-label>Comments</mat-label>
                        <textarea matInput
                                  placeholder="Enter your comments here..."
                                  rows="4"
                                  maxlength="500"
                                  [disabled]="isSubmittingDecision"
                                  [(ngModel)]="decisionComments"></textarea>
                        <mat-hint align="start">Required for rejection decisions</mat-hint>
                        <mat-hint align="end">{{ decisionComments.length || 0 }}/500</mat-hint>
                      </mat-form-field>

                      <div class="decision-actions">
                        <button mat-raised-button
                                color="primary"
                                class="accept-btn"
                                [disabled]="isSubmittingDecision"
                                (click)="onAcceptDecision()">
                          <mat-icon>check_circle</mat-icon>
                          Accept
                        </button>

                        <button mat-raised-button
                                class="reject-action-btn"
                                [disabled]="isSubmittingDecision"
                                (click)="onRejectWithAction()">
                          <mat-icon>report_problem</mat-icon>
                          Reject With Action
                        </button>

                        <button mat-raised-button
                                class="reject-no-action-btn"
                                [disabled]="isSubmittingDecision"
                                (click)="onRejectWithoutAction()">
                          <mat-icon>cancel</mat-icon>
                          Reject Without Action
                        </button>
                      </div>
                    </div>
                  }
                </div>
              }
              }
              }
            </div>
          </section>
        }
      </div>
    </div>
  </div>
</div>
